// ./src/components/modals/ModalCalledAdvance/index.tsx
import React, {
  useCallback,
  useRef,
  useState,
  useMemo,
  useEffect,
} from 'react';

import { FormHandles } from '@unform/core';
import { BiEdit } from 'react-icons/bi';
import { IoCheckmarkCircle, IoClose, IoCloseCircle } from 'react-icons/io5';
import * as Yup from 'yup';

import {
  SubtitleSecondary,
  Paragraph,
  ButtonMain,
  ButtonDefault,
  InputSelect,
  InputMask,
  InputText,
  InputCurrency,
} from '@components/index';
import {
  ConfigLabel,
  ConfigStyles,
  ConfigTransition,
  ConfigValues,
} from '@config/index';
import Address from '@models/Address';
import Called from '@models/Called';
import User from '@models/User';
import GoogleMapsDirectionsService from '@services/apis/apiGoogleMapsDirections';
import { apiIbge, apiRebox } from '@services/index';
import { getValidationErrors } from '@utils/errors';
import { formatCellphone, formatMoney, formatText } from '@utils/formatters';
import { hotToast, toastify } from '@utils/notifiers';
import { validatorEmail } from '@utils/validators';

import Item from './Item';
import {
  schemaInProgress,
  schemaInAttendance,
  schemaDone,
} from './schemaValidation';
import {
  IFormDataInProgress,
  IFormDataInAttendance,
  IFormDataDone,
} from './typing';

import {
  Container,
  ModalAdvance,
  FormAdvance,
  Buttons,
  SectionsGroup,
  SectionsItem,
  SectionsItemGroup,
  ModalProviderList,
  Items,
  Table,
  Labels,
} from './styles';

interface IProps {
  called: Called;
  isOpen: boolean;
  change(): void;
  refresh(): void;
}

const ModalCalledAdvance: React.FC<IProps> = ({
  called: calledData,
  isOpen,
  change,
  refresh,
}) => {
  const formInProgressRef = useRef<FormHandles>(null);
  const formInAttendanceRef = useRef<FormHandles>(null);
  const formDoneRef = useRef<FormHandles>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [
    isOpenProviderListModal,
    setIsOpenProviderListModal,
  ] = useState<boolean>(false);
  const [providerList, setProviderList] = useState<User[]>([]);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [selectedUF, setSelectedUF] = useState<string>('');
  const [selectedCity, setSelectedCity] = useState<string>('');
  const [providerData, setProviderData] = useState({
    name: '',
    cpf: '',
    cnpj: '',
    email: '',
    cellphone: '',
    fullAddress: '',
  });
  const [called, setCalled] = useState<Called>(calledData);
  const [fieldTypeProvider, setFieldTypeProvider] = useState<string>(
    ConfigValues.rebox.default.outhers.called_edit.field_type.user_email,
  );
  const [providerId, setProviderId] = useState<string>('');
  const [alertCheckProvider, setAlertCheckProvider] = useState<number>(0);
  // const [budgetAmount, setBudgetAmount] = useState<any>(
  //   called.budget_amount !== 0 ? called.budget_amount : 0,
  // );
  const [
    isBudgetAmountEdittable,
    setIsBudgetAmountEdittable,
  ] = useState<boolean>(false);

  const [pixPrestador, setPixPrestador] = useState<string | undefined>(
    called.who_is_answering?.bank_accounts?.find(acc => acc.pix_key)?.pix_key ||
      called.service_payment_pix,
  );
  const isPixKeyEditable = Boolean(
    called.who_is_answering?.bank_accounts?.find(acc => acc.pix_key)?.pix_key ||
      called.service_payment_pix,
  );
  const [isPixPrestadorEditable, setIsPixPrestadorEditable] = useState<boolean>(
    isPixKeyEditable,
  );

  const [imeiTrackerDevice, setImeiTrackerDevice] = useState<string>('');

  const [chipTrackerDevice, setChipTrackerDevice] = useState<string>('');

  const nextStatus = useMemo(() => {
    const {
      open,
      in_progress,
      in_attendance,
      done,
    } = ConfigValues.rebox.called.status;
    if (called.status === open) return in_progress;
    if (called.status === in_progress) return in_attendance;
    if (called.status === in_attendance) return done;

    return ``;
  }, [called]);

  const getUserByAddress = async (role: string, fieldType: string) => {
    const typeDoc: string =
      ConfigTransition.rebox_called_field_type[fieldType.toLowerCase()];

    const idHotToast = hotToast(
      `Verificando ${typeDoc.toUpperCase()}`,
      'loading',
    );

    try {
      const {
        data: { data: responseProvider },
      } = await apiRebox.get(
        `/users?role=${role}&state=${selectedUF.toLowerCase()}&city=${selectedCity.toLowerCase()}`,
      );

      setIsOpenProviderListModal(prevStatus => !prevStatus);
      setProviderList(responseProvider);

      hotToast(idHotToast, 'dismiss');
    } catch (error) {
      hotToast(idHotToast, 'dismiss');
    }
  };

  const getUserByName = async (
    value: string,
    role: string,
    fieldType: string,
  ) => {
    const typeDoc: string =
      ConfigTransition.rebox_called_field_type[fieldType.toLowerCase()];

    const idHotToast = hotToast(
      `Verificando ${typeDoc.toUpperCase()}`,
      'loading',
    );
    try {
      const {
        data: { data: responseProvider },
      } = await apiRebox.get(`/users?role=${role}&name=${value}`);

      setIsOpenProviderListModal(prevStatus => !prevStatus);
      setProviderList(responseProvider);

      hotToast(idHotToast, 'dismiss');
    } catch (error) {
      hotToast(idHotToast, 'dismiss');
    }
  };

  const getUserByCpfOrCnpj = async (
    role: string,
    value: string,
    fieldType: string,
  ) => {
    const typeDoc: string =
      ConfigTransition.rebox_called_field_type[fieldType.toLowerCase()];

    const idHotToast = hotToast(
      `Verificando ${typeDoc.toUpperCase()}`,
      'loading',
    );
    try {
      const query = value.length === 11 ? 'cpf' : 'cnpj';
      const {
        data: { data: responseProvider },
      } = await apiRebox.get(`/users?role=${role}&${query}=${value}`);

      setIsOpenProviderListModal(prevStatus => !prevStatus);
      setProviderList(responseProvider);

      hotToast(idHotToast, 'dismiss');
    } catch (error) {
      hotToast(idHotToast, 'dismiss');
    }
  };

  const getUserByEmail = async (
    role: string,
    value: string,
    fieldType: string,
  ) => {
    const typeDoc: string =
      ConfigTransition.rebox_called_field_type[fieldType.toLowerCase()];

    const idHotToast = hotToast(
      `Verificando ${typeDoc.toUpperCase()}`,
      'loading',
    );
    try {
      const {
        data: { data: responseProvider },
      } = await apiRebox.get(`/users?role=${role}&email=${value}`);

      setIsOpenProviderListModal(prevStatus => !prevStatus);
      setProviderList(responseProvider);

      hotToast(idHotToast, 'dismiss');
    } catch (error) {
      hotToast(idHotToast, 'dismiss');
    }
  };

  const getUserByCellphone = async (
    role: string,
    value: string,
    fieldType: string,
  ) => {
    const typeDoc: string =
      ConfigTransition.rebox_called_field_type[fieldType.toLowerCase()];

    const idHotToast = hotToast(
      `Verificando ${typeDoc.toUpperCase()}`,
      'loading',
    );
    try {
      const {
        data: { data: responseProvider },
      } = await apiRebox.get(`/users?role=${role}&cellphone=${value}`);

      setIsOpenProviderListModal(prevStatus => !prevStatus);
      setProviderList(responseProvider);

      hotToast(idHotToast, 'dismiss');
    } catch (error) {
      hotToast(idHotToast, 'dismiss');
    }
  };

  const handleCheckProvider = useCallback(async (provider: User) => {
    setProviderId(provider.id || '');
    setIsOpenProviderListModal(prevStatus => !prevStatus);
    toastify('Prestador confirmado com sucesso', 'success');
  }, []);

  const handleAdvanceToInProgress = useCallback(
    async (data: IFormDataInProgress) => {
      const idHotToast = hotToast(`Iniciando atendimento`, 'loading');
      try {
        setLoading(prevState => !prevState);

        formInProgressRef.current?.setErrors({});

        await schemaInProgress.validate(data, {
          abortEarly: false,
        });

        if (called?.service?.code === 'ser-tracker') {
          if (!data.imei_tracker_device_id) {
            throw new Error('Por favor, informe o número do IMEI.');
          }
          if (!data.chip_tracker_device_number) {
            throw new Error('Por favor, informe o número do chip.');
          }
          if (!data.operator_device_name) {
            throw new Error('Por favor, informe a operadora do chip.');
          }
        }
        const { data: response } = await apiRebox.put(`/called/${called.id}`, {
          users_id: called.users_id,
          contracts_id: called.contracts_id,
          vehicles_id: called.vehicles_id,
          date_created: called.date_created,
          appointment_date: called.appointment_date,
          type: called.type,
          services_id: called.services_id,
          vehicle_situation: called.vehicle_situation,
          source_address_id: called.source_address_id,
          destination_address_id: called.destination_address_id,
          location_type: called.location_type,
          description: called.description,
          budget_amount: called.budget_amount,
          status: ConfigValues.rebox.called.status.in_progress,
          call_initiation_date: null,
          estimated_hours_for_initiation: data.estimated_hours_for_initiation,
          service_start_date: null,
          estimated_hours_for_service_start: null,
          closing_date: null,
          who_is_requesting_id: called.who_is_requesting_id,
          who_is_answering_id: providerId,
          who_is_performing_the_service_id: null,
          technical_report: called.technical_report,
          imei_tracker_device_id: data.imei_tracker_device_id,
          chip_tracker_device_number: data.chip_tracker_device_number,
          operator_device_name: data.operator_device_name,
        });

        const { data: calledUpdated } = response;
        setCalled(calledUpdated);
        toastify(
          'Atendimento ao acionamento foi iniciado com sucesso!',
          'success',
        );
        change();
        refresh();
      } catch (error: any) {
        if (error instanceof Yup.ValidationError) {
          const errors = getValidationErrors(error);

          formInProgressRef.current?.setErrors(errors);

          const {
            estimated_hours_for_initiation,
            field_type_provider,
            who_is_answering,
            // imei_tracker_device_id,
            // chip_tracker_device_number,
            // operator_device_name,
          } = errors;

          if (estimated_hours_for_initiation)
            toastify(estimated_hours_for_initiation, 'error');
          if (field_type_provider) toastify(field_type_provider, 'error');
          if (who_is_answering) toastify(who_is_answering, 'error');
          // if (imei_tracker_device_id) toastify(imei_tracker_device_id, 'error');
          // if (chip_tracker_device_number)
          //   toastify(chip_tracker_device_number, 'error');
          // if (operator_device_name) toastify(operator_device_name, 'error');
        } else if (error?.response) {
          let messageError = `Sinto muito, não foi possível iniciar o atendimento ao chamado.`;
          if (error.response) messageError = error.response.data.error;
          toastify(messageError, `error`);
        } else {
          toastify(error.message, 'error');
        }
      } finally {
        hotToast(idHotToast, 'dismiss');
        setLoading(prevState => !prevState);
      }
    },
    [called, providerId, alertCheckProvider],
  );

  const handleAdvanceToInAttendance = useCallback(
    async (data: IFormDataInAttendance) => {
      const idHotToast = hotToast(`Iniciando prestação de serviço`, 'loading');
      try {
        setLoading(prevState => !prevState);

        formInAttendanceRef.current?.setErrors({});

        await schemaInAttendance.validate(data, {
          abortEarly: false,
        });

        const { data: response } = await apiRebox.put(`/called/${called.id}`, {
          users_id: called.users_id,
          contracts_id: called.contracts_id,
          vehicles_id: called.vehicles_id,
          date_created: called.date_created,
          appointment_date: called.appointment_date,
          type: called.type,
          services_id: called.services_id,
          vehicle_situation: called.vehicle_situation,
          source_address_id: called.source_address_id,
          destination_address_id: called.destination_address_id,
          location_type: called.location_type,
          description: called.description,
          budget_amount: Number(data.budget_amount),
          status: ConfigValues.rebox.called.status.in_attendance,
          call_initiation_date: called.call_initiation_date,
          estimated_hours_for_initiation: called.estimated_hours_for_initiation,
          service_start_date: called.service_start_date || null,
          estimated_hours_for_service_start:
            data.estimated_hours_for_service_start,
          closing_date: null,
          who_is_requesting_id: called.who_is_requesting_id,
          who_is_answering_id: called.who_is_answering_id,
          who_is_performing_the_service_id:
            called.who_is_performing_the_service_id,
          technical_report: called.technical_report,
          who_is_answering_pix: data.pix,
        });

        const { data: calledUpdated } = response;
        setCalled(calledUpdated);
        toastify('Prestação de serviço iniciada com sucesso!', 'success');
        change();
        refresh();
      } catch (error: any) {
        if (error instanceof Yup.ValidationError) {
          const errors = getValidationErrors(error);

          formInAttendanceRef.current?.setErrors(errors);

          const { estimated_hours_for_service_start } = errors;

          if (estimated_hours_for_service_start)
            toastify(estimated_hours_for_service_start, 'error');
        } else {
          let messageError = `Sinto muito, não foi possível iniciar a prestação de serviço.`;
          if (error.response) messageError = error.response.data.error;
          toastify(messageError, `error`);
        }
      } finally {
        hotToast(idHotToast, 'dismiss');
        setLoading(prevState => !prevState);
      }
    },
    [called],
  );

  const handleAdvanceToDone = useCallback(
    async (data: IFormDataDone) => {
      const idHotToast = hotToast(`Encerrando prestação de serviço`, 'loading');
      try {
        setLoading(prevState => !prevState);

        formDoneRef.current?.setErrors({});

        await schemaDone.validate(data, {
          abortEarly: false,
        });

        const { data: response } = await apiRebox.put(`/called/${called.id}`, {
          users_id: called.users_id,
          contracts_id: called.contracts_id,
          vehicles_id: called.vehicles_id,
          date_created: called.date_created,
          appointment_date: called.appointment_date,
          type: called.type,
          services_id: called.services_id,
          vehicle_situation: called.vehicle_situation,
          source_address_id: called.source_address_id,
          destination_address_id: called.destination_address_id,
          location_type: called.location_type,
          description: called.description,
          budget_amount: data.budget_amount,
          status: ConfigValues.rebox.called.status.done,
          call_initiation_date: called.call_initiation_date,
          estimated_hours_for_initiation: called.estimated_hours_for_initiation,
          service_start_date: called.service_start_date,
          estimated_hours_for_service_start:
            called.estimated_hours_for_service_start,
          closing_date: null,
          who_is_requesting_id: called.who_is_requesting_id,
          who_is_answering_id: called.who_is_answering_id,
          who_is_performing_the_service_id:
            called.who_is_performing_the_service_id,
          technical_report: data.technical_report,
          who_is_answering_pix: data.pix,
        });

        const { data: calledUpdated } = response;
        setCalled(calledUpdated);
        toastify('Prestação de serviço finalizada com sucesso!', 'success');
        change();
        return refresh();
      } catch (error: any) {
        if (error instanceof Yup.ValidationError) {
          const errors = getValidationErrors(error);

          formDoneRef.current?.setErrors(errors);

          const { pix, technical_report, budget_amount } = errors;

          if (technical_report) toastify(technical_report, 'error');
          if (budget_amount) toastify(budget_amount, 'error');
          if (pix) toastify(pix, 'error');
        } else {
          let messageError = `Sinto muito, não foi possível encerrar a prestação de serviço.`;
          if (error.response) messageError = error.response.data.error;
          toastify(messageError, `error`);
        }
      } finally {
        hotToast(idHotToast, 'dismiss');
        setLoading(prevState => !prevState);
      }

      return true;
    },
    [called],
  );

  const handleChangeModal = () => {
    setIsOpenProviderListModal(prevState => !prevState);
  };

  const getStates = async () => {
    const { data: responseStates } = await apiIbge(
      'https://servicodados.ibge.gov.br/api/v1/localidades/estados?orderBy=nome',
    );
    const ordenedStates = responseStates.map((state: any) => ({
      value: state.sigla,
      label: state.sigla,
    }));
    setStates(ordenedStates);
  };

  const getCities = async () => {
    const { data: responseCities } = await apiIbge(
      `https://servicodados.ibge.gov.br/api/v1/localidades/estados/${selectedUF}/municipios?orderBy=nome`,
    );
    const ordenedCities = responseCities.map((city: any) => ({
      value: city.nome,
      label: city.nome,
    }));

    setCities(ordenedCities);
  };

  const handleProviderData = ({ value, id }: { value: string; id: string }) => {
    setProviderData(prevData => ({
      ...prevData,
      [id]: value,
    }));
  };

  useEffect(() => {
    getStates();
  }, []);

  useEffect(() => {
    getCities();
  }, [selectedUF]);

  return (
    <Container>
      <ModalAdvance
        isOpen={isOpen}
        onRequestClose={change}
        contentLabel="Advance"
      >
        <ButtonDefault
          iconLeft={IoClose}
          style={{
            position: 'absolute',
            top: 10,
            right: 10,
            maxWidth: 50,
            padding: 0,
          }}
          onClick={change}
        />
        {nextStatus === ConfigValues.rebox.called.status.in_progress && (
          <>
            <SubtitleSecondary>Iniciar atendimento</SubtitleSecondary>
            <FormAdvance
              ref={formInProgressRef}
              onSubmit={handleAdvanceToInProgress}
            >
              <SectionsGroup>
                <SectionsItem>
                  <SubtitleSecondary
                    textAlign="start"
                    nameColor="black"
                    fontSize={14}
                  >
                    Escolher prestador por
                  </SubtitleSecondary>
                  <Paragraph
                    nameColor="black"
                    textAlign="start"
                    opacity={0.5}
                    fontSize={13}
                    style={{ marginBottom: '2vh' }}
                  >
                    Tipo de campo que deseja usar para buscar prestador
                  </Paragraph>
                  <InputSelect
                    name="field_type_provider"
                    placeholder="Selecione"
                    tabIndex={18}
                    options={ConfigLabel.rebox.validation.calledEdit.fieldType}
                    selectedDefault={fieldTypeProvider}
                    onChange={event => {
                      setFieldTypeProvider(event.target.value);
                      setAlertCheckProvider(0);
                      formInProgressRef.current?.setData({
                        who_is_answering: '',
                      });
                    }}
                  />
                </SectionsItem>

                <SectionsItem>
                  <SubtitleSecondary
                    textAlign="start"
                    nameColor="black"
                    fontSize={14}
                  >
                    {`${
                      ConfigTransition.rebox_called_field_type[
                        fieldTypeProvider.toLowerCase()
                      ]
                    } do prestador`}
                  </SubtitleSecondary>
                  <Paragraph
                    nameColor="black"
                    textAlign="start"
                    opacity={0.5}
                    fontSize={13}
                    style={{ marginBottom: '2vh' }}
                  >
                    Define o prestador que será responsável pelo serviço
                  </Paragraph>
                  <SectionsItemGroup>
                    {fieldTypeProvider ===
                      ConfigValues.rebox.default.outhers.called_edit.field_type
                        .user_name && (
                      <InputText
                        name="who_is_answering"
                        id="name"
                        placeholder={`Informe o ${
                          ConfigTransition.rebox_called_field_type[
                            fieldTypeProvider.toLowerCase()
                          ]
                        }`}
                        onChange={({ target }) => {
                          handleProviderData(target);
                        }}
                      />
                    )}
                    {fieldTypeProvider ===
                      ConfigValues.rebox.default.outhers.called_edit.field_type
                        .user_cellphone && (
                      <InputMask
                        name="who_is_answering"
                        placeholder={`Informe o ${
                          ConfigTransition.rebox_called_field_type[
                            fieldTypeProvider.toLowerCase()
                          ]
                        }`}
                        mask="+55 (99) 99999-9999"
                        id="cellphone"
                        value={providerData.cellphone}
                        onChange={({ target }) => {
                          handleProviderData(target);
                        }}
                      />
                    )}
                    {fieldTypeProvider ===
                      ConfigValues.rebox.default.outhers.called_edit.field_type
                        .user_address && (
                      <div
                        style={{
                          margin: '0 auto',
                          width: '100%',
                          gap: '1vh',
                          display: 'flex',
                          flexDirection: 'column',
                        }}
                      >
                        <InputSelect
                          options={states}
                          name="who_is_answering_uf"
                          placeholder="Selecione um estado"
                          onChange={({ target }) => {
                            setSelectedUF(target.value);
                          }}
                        />
                        <InputSelect
                          options={cities}
                          name="who_is_answering_city"
                          id="fullAddress"
                          placeholder="Selecione uma cidade"
                          onChange={({ target }) => {
                            setSelectedCity(target.value);
                            setProviderData(prevData => ({
                              ...prevData,
                              [target.id]: `${selectedUF}-${target.value}`,
                            }));
                          }}
                          style={{
                            cursor: cities.length ? 'pointer' : 'not-allowed',
                            opacity: cities.length ? 1 : 0.5,
                          }}
                        />
                      </div>
                    )}
                    {fieldTypeProvider ===
                      ConfigValues.rebox.default.outhers.called_edit.field_type
                        .user_cpf && (
                      <InputMask
                        name="who_is_answering"
                        placeholder={`Informe o ${
                          ConfigTransition.rebox_called_field_type[
                            fieldTypeProvider.toLowerCase()
                          ]
                        }`}
                        mask={
                          fieldTypeProvider ===
                          ConfigValues.rebox.default.outhers.called_edit
                            .field_type.user_cpf
                            ? '999.999.999-99'
                            : '99.999.999/9999-99'
                        }
                        tabIndex={19}
                        value={providerData.cpf}
                        id="cpf"
                        onChange={({ target }) => {
                          handleProviderData(target);
                        }}
                      />
                    )}
                    {fieldTypeProvider ===
                      ConfigValues.rebox.default.outhers.called_edit.field_type
                        .user_cnpj && (
                      <InputMask
                        name="who_is_answering"
                        placeholder={`Informe o ${
                          ConfigTransition.rebox_called_field_type[
                            fieldTypeProvider.toLowerCase()
                          ]
                        }`}
                        mask={
                          fieldTypeProvider ===
                          ConfigValues.rebox.default.outhers.called_edit
                            .field_type.user_cpf
                            ? '999.999.999-99'
                            : '99.999.999/9999-99'
                        }
                        tabIndex={19}
                        id="cnpj"
                        value={providerData.cnpj}
                        onChange={({ target }) => {
                          handleProviderData(target);
                        }}
                      />
                    )}
                    {fieldTypeProvider ===
                      ConfigValues.rebox.default.outhers.called_edit.field_type
                        .user_email && (
                      <InputText
                        name="who_is_answering"
                        placeholder="Informe o e-mail"
                        tabIndex={19}
                        id="email"
                        onChange={({ target }) => {
                          handleProviderData(target);
                        }}
                      />
                    )}
                  </SectionsItemGroup>
                  <ButtonMain
                    type="submit"
                    style={{ marginTop: '2vh' }}
                    onClick={event => {
                      event.preventDefault();
                      if (providerData.name) {
                        getUserByName(
                          providerData.name,
                          ConfigValues.rebox.user.role.provider,
                          fieldTypeProvider,
                        );
                      }
                      if (providerData.cpf || providerData.cnpj) {
                        getUserByCpfOrCnpj(
                          ConfigValues.rebox.user.role.provider,
                          providerData.cpf || providerData.cnpj,
                          fieldTypeProvider,
                        );
                      }
                      if (providerData.email) {
                        getUserByEmail(
                          ConfigValues.rebox.user.role.provider,
                          providerData.email,
                          fieldTypeProvider,
                        );
                      }
                      if (providerData.cellphone) {
                        getUserByCellphone(
                          ConfigValues.rebox.user.role.provider,
                          providerData.cellphone,
                          fieldTypeProvider,
                        );
                      }
                      if (providerData.fullAddress) {
                        getUserByAddress(
                          ConfigValues.rebox.user.role.provider,
                          fieldTypeProvider,
                        );
                      }
                    }}
                  >
                    Buscar
                  </ButtonMain>
                </SectionsItem>
              </SectionsGroup>
              <SectionsGroup>
                <SectionsItem>
                  <SubtitleSecondary
                    textAlign="start"
                    nameColor="black"
                    fontSize={14}
                  >
                    Estimativa de chegada
                  </SubtitleSecondary>
                  <Paragraph
                    nameColor="black"
                    textAlign="start"
                    opacity={0.5}
                    fontSize={13}
                    style={{ marginBottom: '2vh' }}
                  >
                    Tempo estimado para a chegada do prestador até o cliente
                  </Paragraph>
                  <InputSelect
                    name="estimated_hours_for_initiation"
                    options={
                      ConfigLabel.rebox.others.called
                        .estimatedHoursForInitiation
                    }
                    placeholder="Selecione"
                    tabIndex={14}
                  />
                </SectionsItem>
              </SectionsGroup>
              {called?.service?.code === 'ser-tracker' && (
                <>
                  <SectionsGroup>
                    <SectionsItem>
                      <SubtitleSecondary
                        textAlign="start"
                        nameColor="black"
                        fontSize={14}
                      >
                        Número do IMEI
                      </SubtitleSecondary>
                      <Paragraph
                        nameColor="black"
                        textAlign="start"
                        opacity={0.5}
                        fontSize={13}
                        style={{ marginBottom: '2vh' }}
                      >
                        Número identificador do dispositivo
                      </Paragraph>
                      <InputText
                        name="imei_tracker_device_id"
                        placeholder="Número do IMEI"
                        tabIndex={14}
                      />
                    </SectionsItem>
                  </SectionsGroup>
                  <SectionsGroup>
                    <SectionsItem>
                      <SubtitleSecondary
                        textAlign="start"
                        nameColor="black"
                        fontSize={14}
                      >
                        Número do Chip
                      </SubtitleSecondary>
                      <Paragraph
                        nameColor="black"
                        textAlign="start"
                        opacity={0.5}
                        fontSize={13}
                        style={{ marginBottom: '2vh' }}
                      >
                        Número identificador do Chip
                      </Paragraph>
                      <InputText
                        name="chip_tracker_device_number"
                        placeholder="Número do Chip"
                        tabIndex={14}
                      />
                    </SectionsItem>
                  </SectionsGroup>
                  <SectionsGroup>
                    <SectionsItem>
                      <SubtitleSecondary
                        textAlign="start"
                        nameColor="black"
                        fontSize={14}
                      >
                        Operadora do Chip
                      </SubtitleSecondary>
                      <Paragraph
                        nameColor="black"
                        textAlign="start"
                        opacity={0.5}
                        fontSize={13}
                        style={{ marginBottom: '2vh' }}
                      >
                        Operadora do chip do rastreador
                      </Paragraph>
                      <InputText
                        name="operator_device_name"
                        placeholder="Nome da operadora do chip"
                        tabIndex={14}
                      />
                    </SectionsItem>
                  </SectionsGroup>
                </>
              )}

              <Buttons>
                <ButtonMain
                  loading={loading}
                  type={`submit`}
                  style={{
                    marginRight: '1vw',
                    pointerEvents: loading ? 'none' : 'auto',
                  }}
                >
                  Iniciar
                </ButtonMain>
                <ButtonDefault type="button" onClick={change}>
                  Voltar
                </ButtonDefault>
              </Buttons>
            </FormAdvance>
          </>
        )}

        {nextStatus === ConfigValues.rebox.called.status.in_attendance && (
          <>
            <SubtitleSecondary>Iniciar prestação de serviço</SubtitleSecondary>
            <FormAdvance
              ref={formInAttendanceRef}
              onSubmit={handleAdvanceToInAttendance}
            >
              <SectionsGroup>
                <SectionsItem>
                  <SubtitleSecondary
                    textAlign="start"
                    nameColor="black"
                    fontSize={14}
                  >
                    Estimativa de finalização
                  </SubtitleSecondary>
                  <Paragraph
                    nameColor="black"
                    textAlign="start"
                    opacity={0.5}
                    fontSize={13}
                    style={{ marginBottom: '2vh' }}
                  >
                    Tempo estimado para prestador terminar o serviço
                  </Paragraph>

                  <InputSelect
                    name="estimated_hours_for_service_start"
                    options={
                      ConfigLabel.rebox.others.called
                        .estimatedHoursForServiceStart
                    }
                    placeholder="Selecione"
                    tabIndex={16}
                  />
                </SectionsItem>

                <SectionsItem>
                  <SubtitleSecondary
                    textAlign="start"
                    nameColor="black"
                    fontSize={14}
                  >
                    Qual o pix do prestador?
                  </SubtitleSecondary>
                  <Paragraph
                    nameColor="black"
                    textAlign="start"
                    opacity={0.5}
                    fontSize={13}
                    style={{ marginBottom: '2vh' }}
                  >
                    Informar pix para pagamento da remoção
                  </Paragraph>
                  <div
                    style={{
                      display: 'flex',
                      gap: '1vw',
                      alignItems: 'center',
                    }}
                  >
                    <InputText
                      name="pix"
                      style={{
                        cursor: isPixPrestadorEditable ? 'not-allowed' : 'text',
                        opacity: isPixPrestadorEditable ? 0.5 : 1,
                      }}
                      value={pixPrestador}
                      disabled={isPixPrestadorEditable}
                      placeholder="Informe a chave pix"
                      onChange={e => {
                        setPixPrestador(e.target.value);
                      }}
                    />
                    <BiEdit
                      size={22}
                      title="icone de editar"
                      color={ConfigStyles.rebox.colors.black.main}
                      onClick={() =>
                        setIsPixPrestadorEditable(prevStatus => !prevStatus)
                      }
                    />
                  </div>
                </SectionsItem>

                <SectionsItem>
                  <SubtitleSecondary
                    nameColor="black"
                    textAlign="start"
                    fontSize={14}
                  >
                    Qual o valor do acionamento
                  </SubtitleSecondary>
                  <Paragraph
                    nameColor="black"
                    textAlign="start"
                    opacity={0.5}
                    fontSize={13}
                    style={{ marginBottom: '2vh' }}
                  >
                    Valor estipulado pelo prestador para o serviço
                  </Paragraph>
                  <InputText
                    name="budget_amount"
                    placeholder="Informe o valor do acionamento"
                    onChange={({ target }) => {
                      // setBudgetAmount(Number(target.value));
                    }}
                  />
                </SectionsItem>
              </SectionsGroup>
              <Buttons>
                <ButtonMain
                  loading={loading}
                  type={`submit`}
                  style={{ marginRight: '1vw' }}
                >
                  Iniciar
                </ButtonMain>
                <ButtonDefault type="button" onClick={change}>
                  Voltar
                </ButtonDefault>
              </Buttons>
            </FormAdvance>
          </>
        )}

        {nextStatus === ConfigValues.rebox.called.status.done && (
          <>
            <SubtitleSecondary>
              Finalizar prestação de serviço
            </SubtitleSecondary>
            <FormAdvance ref={formDoneRef} onSubmit={handleAdvanceToDone}>
              <SectionsGroup>
                <SectionsItem>
                  <SubtitleSecondary
                    textAlign="start"
                    nameColor="black"
                    fontSize={14}
                  >
                    Parecer técnico
                  </SubtitleSecondary>
                  <Paragraph
                    nameColor="black"
                    textAlign="start"
                    opacity={0.5}
                    fontSize={13}
                    style={{ marginBottom: '2vh' }}
                  >
                    Observação feita pelo colaborador que atendeu ao chamado
                  </Paragraph>

                  <InputText
                    name="technical_report"
                    placeholder="Descreva aqui"
                    tabIndex={22}
                  />
                </SectionsItem>

                <SectionsItem>
                  <SubtitleSecondary
                    textAlign="start"
                    nameColor="black"
                    fontSize={14}
                  >
                    Valor do acionamento
                  </SubtitleSecondary>
                  <Paragraph
                    nameColor="black"
                    textAlign="start"
                    opacity={0.5}
                    fontSize={13}
                    style={{ marginBottom: '2vh' }}
                  >
                    Valor estipulado pelo prestador para o serviço
                  </Paragraph>
                  <div
                    style={{
                      display: 'flex',
                      gap: '1vw',
                      alignItems: 'center',
                    }}
                  >
                    <InputText
                      name="budget_amount"
                      placeholder="Valor do acionamento"
                      style={{
                        cursor: !isBudgetAmountEdittable
                          ? 'not-allowed'
                          : 'text',
                        opacity: !isBudgetAmountEdittable ? 0.5 : 1,
                      }}
                      disabled={!isBudgetAmountEdittable}
                      onChange={({ target }) => {
                        const [budgetValue] = target.value.split(',');
                        // setBudgetAmount(budgetValue);
                      }}
                    />
                    <BiEdit
                      size={22}
                      title="icone de editar"
                      color={ConfigStyles.rebox.colors.black.main}
                      onClick={() =>
                        setIsBudgetAmountEdittable(prevStatus => !prevStatus)
                      }
                    />
                  </div>
                </SectionsItem>

                <SectionsItem>
                  <SubtitleSecondary
                    textAlign="start"
                    nameColor="black"
                    fontSize={14}
                  >
                    Qual o pix do prestador?
                  </SubtitleSecondary>
                  <Paragraph
                    nameColor="black"
                    textAlign="start"
                    opacity={0.5}
                    fontSize={13}
                    style={{ marginBottom: '2vh' }}
                  >
                    Informar pix para pagamento da remoção
                  </Paragraph>
                  <div
                    style={{
                      display: 'flex',
                      gap: '1vw',
                      alignItems: 'center',
                    }}
                  >
                    <InputText
                      name="pix"
                      style={{
                        cursor: isPixPrestadorEditable ? 'not-allowed' : 'text',
                        opacity: isPixPrestadorEditable ? 0.5 : 1,
                      }}
                      value={pixPrestador}
                      disabled={isPixPrestadorEditable}
                      placeholder="Informe a chave pix"
                      onChange={e => {
                        setPixPrestador(e.target.value);
                      }}
                    />
                    <BiEdit
                      size={22}
                      title="icone de editar"
                      color={ConfigStyles.rebox.colors.black.main}
                      onClick={() =>
                        setIsPixPrestadorEditable(prevStatus => !prevStatus)
                      }
                    />
                  </div>
                </SectionsItem>
              </SectionsGroup>
              <Buttons>
                <ButtonMain
                  loading={loading}
                  type={`submit`}
                  style={{ marginRight: '1vw' }}
                >
                  Finalizar
                </ButtonMain>
                <ButtonDefault type="button" onClick={change}>
                  Voltar
                </ButtonDefault>
              </Buttons>
            </FormAdvance>
          </>
        )}
        {isOpenProviderListModal && (
          <ModalProviderList
            isOpen={isOpenProviderListModal}
            onRequestClose={handleChangeModal}
            contentLabel="Advance"
          >
            <ButtonDefault
              iconLeft={IoClose}
              style={{
                position: 'absolute',
                top: 10,
                right: 10,
                maxWidth: 50,
                padding: 0,
              }}
              onClick={handleChangeModal}
            />
            <Container>
              <SubtitleSecondary nameColor="black" textAlign="start">
                Lista de prestadores encontrados
              </SubtitleSecondary>
              <Table>
                <Labels>
                  <Paragraph nameColor="black" textAlign="start" fontSize={12}>
                    Nome
                  </Paragraph>
                  <Paragraph nameColor="black" textAlign="start" fontSize={12}>
                    Celular
                  </Paragraph>
                  <Paragraph nameColor="black" textAlign="start" fontSize={12}>
                    Base/Bairro
                  </Paragraph>
                  <Paragraph nameColor="black" textAlign="start" fontSize={12}>
                    Distância até o cliente
                  </Paragraph>
                  <Paragraph nameColor="black" textAlign="start" fontSize={12}>
                    KMs totais
                  </Paragraph>
                  <Paragraph nameColor="black" textAlign="start" fontSize={12}>
                    Valor do serviço
                  </Paragraph>
                  <Paragraph
                    nameColor="black"
                    textAlign="start"
                    fontSize={12}
                  ></Paragraph>
                </Labels>
              </Table>
              <Items>
                {providerList.map(provider => (
                  <>
                    <Item
                      key={provider.id}
                      user={provider}
                      distanceInKm={called.distance_between_points_in_km}
                      handleConfirmProvider={handleCheckProvider}
                    />
                  </>
                ))}
              </Items>
            </Container>
          </ModalProviderList>
        )}
      </ModalAdvance>
    </Container>
  );
};

export default ModalCalledAdvance;
